/**
 * Phase 6A: User Management Wrapper with Fallback
 *
 * Centralizes all user_profiles and user_gym_assignments operations through
 * the user-management edge function with fallback to legacy direct calls.
 *
 * Feature flags control activation for safe staged rollout.
 *
 * ⚠️ Ce fichier embarquait sa PROPRE copie d'`invokeWithRetry`, restée sur la
 * version boguée (test de `error.status`, jamais renseigné par supabase-js, donc
 * retry et déconnexion morts ; `error: null` renvoyé sur session expirée). Il
 * utilise désormais `common/invokeWithRetry`, seule implémentation maintenue, qui
 * distingue explicitement 401 (session) et 403 (droits).
 */

import { supabase } from '@/integrations/supabase/client';
import i18n from '@/i18n';
import { invokeWithRetry, type InvokeError } from './common/invokeWithRetry';
import { isTransportFailure, isLocalWriteFallbackAllowed } from './common/isTransportFailure';

// ========================================
// FEATURE FLAGS
// ========================================

const FEATURE_FLAGS = {
  // User profiles operations
  useEdgeFunctionForGetProfile: true,
  useEdgeFunctionForUpdateProfile: true,

  // User gym assignments operations
  useEdgeFunctionForGetAssignments: true,
  useEdgeFunctionForUpdateAssignments: true,
};

/** Message d'erreur exploitable même quand `error` est absent. */
function toError(error: InvokeError | null | undefined, fallbackMessage: string): Error {
  return error ?? new Error(fallbackMessage);
}

// Enable all flags at once (for testing or production activation)
export function enableAllUserManagementFlags() {
  Object.keys(FEATURE_FLAGS).forEach(key => {
    FEATURE_FLAGS[key as keyof typeof FEATURE_FLAGS] = true;
  });
  console.log('✅ All user management edge function flags enabled');
}

// Disable all flags (emergency rollback)
export function disableAllUserManagementFlags() {
  Object.keys(FEATURE_FLAGS).forEach(key => {
    FEATURE_FLAGS[key as keyof typeof FEATURE_FLAGS] = false;
  });
  console.log('⚠️ All user management edge function flags disabled');
}

// ========================================
// WRAPPER FUNCTIONS
// ========================================
//
// Règle commune à toutes les fonctions ci-dessous : un repli `…Legacy()` (qui
// lit/écrit la table DIRECTEMENT depuis le navigateur) ne doit se produire que
// si la fonction edge n'a pas pu se prononcer — panne réseau, DNS, CORS,
// ou 5xx. C'est exactement le critère de `common/isTransportFailure`, partagé
// avec les autres wrappers pour qu'il n'en existe qu'une définition.
//
// Nuance pour les ÉCRITURES (`updateUserProfile`, `updateUserGymAssignments`) :
// elles utilisent `isLocalWriteFallbackAllowed`, qui exclut en plus le
// dépassement du délai client. Un timeout ne veut pas dire « la fonction edge
// n'a rien écrit », il veut dire « elle n'a pas encore répondu » : rejouer
// l'écriture depuis le navigateur pendant que le serveur la termine produirait
// deux écritures pour une seule action. Les LECTURES gardent
// `isTransportFailure` : relire deux fois est sans conséquence.
//
// Dès que le serveur a rendu une décision (400, 403, 404, erreur métier en 200),
// elle fait autorité : rejouer la même opération depuis le navigateur, avec les
// seules politiques RLS comme garde-fou, reviendrait à contourner un refus
// explicite. C'est d'autant plus critique que la fonction edge renvoie
// désormais 403 (et non plus 401) sur un refus de droits : l'ancien `try/catch`
// global basculait sur le repli navigateur sans jamais regarder POURQUOI
// l'appel avait échoué.

/**
 * Get user profile
 *
 * Contrat conservé : `null` quand la session a expiré (la déconnexion et la
 * redirection sont déjà lancées par `invokeWithRetry`), lève sinon en cas
 * d'échec. `userProfileService.getUserProfile` intercepte et renvoie `null`.
 */
export async function getUserProfile(
  userId: string,
  gymId: string
): Promise<any> {
  if (!FEATURE_FLAGS.useEdgeFunctionForGetProfile) {
    return getUserProfileLegacy(userId, gymId);
  }

  console.log('[UserManagement] Using edge function: get_user_profile');

  const result = await invokeWithRetry<{ profile?: any }>('user-management', {
    action: 'get_user_profile',
    gym_id: gymId,
    userId,
  });

  // Session expirée : déconnexion déjà effectuée, inutile de tenter quoi que ce
  // soit d'autre (un appel direct échouerait aussi, faute de jeton).
  if (result.authExpired) {
    return null;
  }

  // 403 : le serveur a refusé les droits. Pas de repli, on remonte le refus.
  if (result.forbidden) {
    throw toError(result.error, i18n.t("userManagement.profileAccessDenied", { ns: "appui" }));
  }

  if (result.error) {
    if (isTransportFailure(result.error, result)) {
      console.error('[UserManagement] Edge function unreachable, falling back to direct call:', result.error);
      return getUserProfileLegacy(userId, gymId);
    }
    // Erreur métier / refus : la décision vient du serveur, on la propage au
    // lieu de la contourner par une lecture directe.
    throw result.error;
  }

  if (!result.data?.profile) throw new Error('No profile returned from edge function');

  return result.data.profile;
}

async function getUserProfileLegacy(userId: string, gymId: string): Promise<any> {
  console.log('[UserManagement] Using legacy direct call: get_user_profile');

  // Verify user belongs to gym
  const { data: membership } = await supabase
    .from('user_gym_assignments')
    .select('gym_id')
    .eq('user_id', userId)
    .eq('gym_id', gymId)
    .maybeSingle();

  if (!membership) {
    throw new Error('User not found in this gym');
  }

  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      throw new Error('User not found');
    }
    throw error;
  }

  return data;
}

/**
 * Update user profile
 *
 * Contrat conservé : `null` sur session expirée, lève sur tout autre échec.
 * `userProfileService.updateUserProfile` teste `profile != null` et intercepte
 * les exceptions : les deux chemins retournent donc bien « échec ».
 */
export async function updateUserProfile(
  userId: string,
  gymId: string,
  profileData: any
): Promise<any> {
  if (!FEATURE_FLAGS.useEdgeFunctionForUpdateProfile) {
    return updateUserProfileLegacy(userId, gymId, profileData);
  }

  console.log('[UserManagement] Using edge function: update_user_profile');

  const result = await invokeWithRetry<{ profile?: any }>('user-management', {
    action: 'update_user_profile',
    gym_id: gymId,
    userId,
    profileData,
  });

  if (result.authExpired) return null;

  // 403 « Admin role required » : surtout pas de repli, sinon on rejouerait
  // l'écriture depuis le navigateur alors que le serveur vient de la refuser.
  if (result.forbidden) {
    throw toError(result.error, i18n.t("userManagement.profileUpdateDenied", { ns: "appui" }));
  }

  if (result.error) {
    if (isLocalWriteFallbackAllowed(result.error, result)) {
      console.error('[UserManagement] Edge function unreachable, falling back to direct call:', result.error);
      return updateUserProfileLegacy(userId, gymId, profileData);
    }
    throw result.error;
  }

  if (!result.data?.profile) throw new Error('No profile returned from edge function');

  return result.data.profile;
}

async function updateUserProfileLegacy(userId: string, gymId: string, profileData: any): Promise<any> {
  console.log('[UserManagement] Using legacy direct call: update_user_profile');

  // ⚠️ Ce chemin ne peut PAS rejouer le contrôle de rôle admin de la fonction
  // edge (le navigateur n'a que le JWT de l'appelant) : la seule protection
  // restante est RLS. Il n'est donc emprunté que lorsque le serveur n'a pas
  // répondu du tout, jamais sur un refus.
  const { data: membership } = await supabase
    .from('user_gym_assignments')
    .select('gym_id')
    .eq('user_id', userId)
    .eq('gym_id', gymId)
    .maybeSingle();

  if (!membership) {
    throw new Error('User not found in this gym');
  }

  const { data, error } = await supabase
    .from('user_profiles')
    .update(profileData)
    .eq('id', userId)
    .select();

  if (error) throw error;

  // Un UPDATE filtré par RLS ne renvoie PAS d'erreur : il renvoie simplement
  // zéro ligne. Sans ce contrôle, un refus de la base passerait pour un succès
  // et l'interface afficherait « Profil mis à jour ».
  if (!data || data.length === 0) {
    throw new Error(i18n.t("userManagement.profileUpdateNoRows", { ns: "appui" }));
  }

  return data[0];
}

/**
 * Get user gym assignments
 *
 * `[]` sur session expirée (redirection en cours), lève sur refus ou erreur
 * serveur : renvoyer une liste vide sur un 403 ferait croire à « aucune
 * affectation » alors que la lecture a été refusée.
 */
export async function getUserGymAssignments(
  userId: string,
  gymId: string,
  targetUserId?: string
): Promise<any[]> {
  if (!FEATURE_FLAGS.useEdgeFunctionForGetAssignments) {
    return getUserGymAssignmentsLegacy(userId, targetUserId);
  }

  console.log('[UserManagement] Using edge function: get_user_gym_assignments');

  const result = await invokeWithRetry<{ assignments?: any[] }>('user-management', {
    action: 'get_user_gym_assignments',
    gym_id: gymId,
    targetUserId,
  });

  if (result.authExpired) return [];

  if (result.forbidden) {
    throw toError(result.error, i18n.t("userManagement.assignmentsReadDenied", { ns: "appui" }));
  }

  if (result.error) {
    if (isTransportFailure(result.error, result)) {
      console.error('[UserManagement] Edge function unreachable, falling back to direct call:', result.error);
      return getUserGymAssignmentsLegacy(userId, targetUserId);
    }
    throw result.error;
  }

  if (!result.data?.assignments) throw new Error('No assignments returned from edge function');

  return result.data.assignments;
}

async function getUserGymAssignmentsLegacy(userId: string, targetUserId?: string): Promise<any[]> {
  console.log('[UserManagement] Using legacy direct call: get_user_gym_assignments');

  const requestedUserId = targetUserId || userId;

  const { data, error } = await supabase
    .from('user_gym_assignments')
    .select('*')
    .eq('user_id', requestedUserId);

  if (error) throw error;

  return data || [];
}

/**
 * Update user gym assignments (e.g., allowed_courses)
 *
 * ⚠️ Changement de contrat : cette fonction levait uniquement via le `catch`
 * autour de l'ancien wrapper, et renvoyait `null` sur session expirée. Or son
 * seul appelant (`AllowedCoursesSelector`) affiche « Cours autorisés mis à
 * jour » dès qu'aucune exception n'est levée : un refus 403 ou une session
 * perdue produisaient donc un succès mensonger. On lève désormais dans tous les
 * cas d'échec, session expirée comprise.
 */
export async function updateUserGymAssignments(
  userId: string,
  gymId: string,
  targetUserId: string,
  assignmentData: any
): Promise<any> {
  if (!FEATURE_FLAGS.useEdgeFunctionForUpdateAssignments) {
    return updateUserGymAssignmentsLegacy(targetUserId, gymId, assignmentData);
  }

  console.log('[UserManagement] Using edge function: update_user_gym_assignments');

  const result = await invokeWithRetry<{ assignment?: any }>('user-management', {
    action: 'update_user_gym_assignments',
    gym_id: gymId,
    targetUserId,
    assignmentData,
  });

  if (result.authExpired) {
    throw toError(result.error, i18n.t("userManagement.assignmentsSessionExpired", { ns: "appui" }));
  }

  if (result.forbidden) {
    throw toError(result.error, i18n.t("userManagement.assignmentsUpdateDenied", { ns: "appui" }));
  }

  if (result.error) {
    if (isLocalWriteFallbackAllowed(result.error, result)) {
      console.error('[UserManagement] Edge function unreachable, falling back to direct call:', result.error);
      return updateUserGymAssignmentsLegacy(targetUserId, gymId, assignmentData);
    }
    throw result.error;
  }

  if (!result.data?.assignment) throw new Error('No assignment returned from edge function');

  return result.data.assignment;
}

async function updateUserGymAssignmentsLegacy(
  targetUserId: string,
  gymId: string,
  assignmentData: any
): Promise<any> {
  console.log('[UserManagement] Using legacy direct call: update_user_gym_assignments');

  // ⚠️ Comme pour le profil : le contrôle « rôle admin » de la fonction edge
  // n'est pas rejouable ici, seul RLS protège l'écriture. Chemin réservé aux
  // pannes de transport.
  const { data: membership } = await supabase
    .from('user_gym_assignments')
    .select('id')
    .eq('user_id', targetUserId)
    .eq('gym_id', gymId)
    .maybeSingle();

  if (!membership) {
    throw new Error('User not found in this gym');
  }

  const { data, error } = await supabase
    .from('user_gym_assignments')
    .update(assignmentData)
    .eq('user_id', targetUserId)
    .eq('gym_id', gymId)
    .select();

  if (error) throw error;

  // Idem : zéro ligne touchée = refus RLS silencieux, pas un succès.
  if (!data || data.length === 0) {
    throw new Error(i18n.t("userManagement.assignmentsUpdateNoRows", { ns: "appui" }));
  }

  return data[0];
}
